import { AgentChatInput } from '@/components/AgentChat/AgentChatInput.tsx';
import { AgentChatMessages } from '@/components/AgentChat/AgentChatMessages';
import { AgentToolbar } from '@/components/AgentChat/AgentToolbar';
import { DynamicPageDraftCard } from '@/components/AgentChat/DynamicPageDraftCard';
import { StillAnalyzingAgentChatInputWrapper } from '@/components/AgentChat/StillAnalyzingAgentChatInputWrapper';
import { DynamicPageSettingsModal } from '@/components/ManageDynamicPage/DynamicPageSettingsModal';
import { AgentChatProvider } from '@/contexts/AgentChatContext';
import { useWhitelabel } from '@/contexts/WhitelabelContext';
import { useAiAssistantLogic } from '@/hooks/assistant/useAiAssistantLogic';
import EditVariantConstraintsDraftModal from '@/modals/EditVariantConstraintsDraftModal';
import EditVariantDraftModal from '@/modals/EditVariantDraftModal';
import NewEditorModal from '@/modals/NewEditorModal';
import { useVariantOriginData } from '@/modals/VariantOriginModal/useVariantOriginData';
import VariantOriginModal from '@/modals/VariantOriginModal/VariantOriginModal';
import AlertModal from '@superfunnel/ui-core/AlertModal';

function AiAssistantContent() {
  const { logoIcon } = useWhitelabel();
  const {
    // Chat state
    hasMessages,
    isWorking,
    isLoading,
    isStreaming,
    lastUpdated,
    messages,

    structureData,

    // Chat actions
    sendMessage,
    stopStreaming,
    resetAgentSession,

    // Prompt context
    promptContexts,
    setPromptContexts,
    updatePromptContext,
    addPromptContext,
    userMessageHistory,

    // Editor modal
    editorModal,
    closeEditor,

    // DynamicPageDraftCard actions
    handleEditVariant,
    handleEditConstraints,
    handleDeleteVariant,
    handleSettings,
    handleGoToPage,
    generatingVariantId,
    isDraftCardDisabled,

    // Edit variant modal
    editVariantModal,
    closeEditVariantModal,
    saveEditVariantModal,

    // Edit constraints modal
    editConstraintsModal,
    closeEditConstraintsModal,
    saveEditConstraintsModal,

    // Delete variant modal
    deleteVariantModal,
    closeDeleteVariantModal,
    confirmDeleteVariant,

    // Variant origin modal
    variantOriginModal,
    openVariantOriginModal,
    closeVariantOriginModal,
    handleVariantOriginSelect,

    // Settings modal
    settingsModalOpen,
    closeSettingsModal,
    handleUpdatePage,
    handleUpdateMetadata,
    dynamicPageId,
    dynamicPageData,
  } = useAiAssistantLogic();

  // Fetch all dynamic pages with variants for the origin modal
  const { dynamicPagesWithVariants, isLoading: isLoadingDynamicPages } =
    useVariantOriginData();

  return (
    <>
      <NewEditorModal
        dynamicPageId={editorModal?.dynamicPageId || ''}
        initialVariantId={editorModal?.variantId || ''}
        initialWebsiteId={editorModal?.websiteId || ''}
        initialMessage={editorModal?.initialMessage}
        open={editorModal !== undefined}
        onClose={closeEditor}
      />
      <EditVariantDraftModal
        initialName={editVariantModal?.name || ''}
        initialDescription={editVariantModal?.description || ''}
        initialPath={editVariantModal?.path || ''}
        siblingPaths={
          structureData?.variants
            ?.filter((v) => v.id !== editVariantModal?.variantId)
            .map((v) => v.path)
            .filter((p): p is string => !!p) || []
        }
        open={editVariantModal !== undefined}
        onClose={closeEditVariantModal}
        onSave={saveEditVariantModal}
      />
      <EditVariantConstraintsDraftModal
        initialConstraints={editConstraintsModal?.constraints || {}}
        open={editConstraintsModal !== undefined}
        onClose={closeEditConstraintsModal}
        onSave={saveEditConstraintsModal}
      />
      <DynamicPageSettingsModal
        open={settingsModalOpen}
        setOpen={closeSettingsModal}
        dynamicPageId={dynamicPageId}
        dynamicPage={dynamicPageData as any}
        onUpdatePage={handleUpdatePage}
        onUpdateMetadata={handleUpdateMetadata}
        visibleTabs={[
          'general',
          'design',
          'search-and-social',
          'custom-code',
          'tracking',
        ]}
      />
      <AlertModal
        open={deleteVariantModal !== undefined}
        onClose={closeDeleteVariantModal}
        title={`Delete "${deleteVariantModal?.variantName}"?`}
        description="This action cannot be undone. The variant will be permanently removed."
        type="warn"
        actions={[
          {
            title: 'Cancel',
            type: 'secondary',
            onClick: closeDeleteVariantModal,
          },
          {
            title: 'Delete',
            type: 'primary',
            color: 'negative',
            onClick: confirmDeleteVariant,
          },
        ]}
      />
      <VariantOriginModal
        open={variantOriginModal !== undefined}
        onClose={closeVariantOriginModal}
        onSelect={handleVariantOriginSelect}
        mode="variant"
        title="Create a new variant"
        subtitle="Choose how you want to start your new variant"
        currentPageVariants={structureData?.variants?.map((v) => ({
          id: v.id,
          name: v.name || 'Untitled Variant',
          description: v.description,
          status: v.status || 'draft',
          websiteIds: v.createdWebsiteId ? [v.createdWebsiteId] : [],
        }))}
        currentDynamicPageId={dynamicPageId}
        currentDynamicPageName={dynamicPageData?.name || ''}
        allDynamicPages={dynamicPagesWithVariants}
        disabled={isLoadingDynamicPages}
        defaultPrompt={variantOriginModal?.defaultPrompt}
      />

      <div
        className={
          'flex h-screen min-h-full w-full min-w-full items-center justify-center bg-[linear-gradient(180deg,rgba(215,208,255,0.2)_0%,rgba(203,221,255,0.5)_100%)] lg:h-screen lg:w-full'
        }
      >
        <div className="max-w-186 relative flex h-full w-full flex-col">
          {/* Agent Toolbar - fixed to top of content area, slides in when messages exist */}
          <div
            className={`absolute left-0 right-0 top-4 z-50 mx-auto transition-all duration-700 ease-out ${
              hasMessages
                ? 'pointer-events-auto translate-y-0 opacity-100'
                : 'pointer-events-none -translate-y-[calc(100%+1rem)] opacity-0'
            }`}
          >
            <AgentToolbar
              title="AI Assistant"
              onNewChat={resetAgentSession}
              disabled={isLoading || isStreaming}
            />
          </div>
          {/* Spacer - grows when no messages to center content */}
          <div
            className={`transition-all duration-700 ease-in-out ${
              hasMessages ? 'flex-none' : 'flex-1'
            }`}
          />

          {/* Header - Show only when no messages */}
          <div
            className={`overflow-visible transition-all duration-700 ease-in-out ${
              hasMessages
                ? 'pointer-events-none max-h-0 scale-95 opacity-0'
                : 'max-h-104 scale-100 opacity-100'
            }`}
          >
            <div className="flex flex-col items-center gap-2.5 text-center">
              <img src={logoIcon} alt="logo" className={'w-12'} />
              <h1 className="text-3xl font-medium -tracking-[0.01em] lg:text-[38px]/[1.2]">
                Let&apos;s make{' '}
                <span className={'text-brand-500'}> high-converting </span>
                <br />
                landing page in minutes!
              </h1>
            </div>
          </div>

          {/* Gap between header and input when no messages */}
          <div
            className={`transition-all duration-700 ease-in-out ${
              hasMessages ? 'h-0' : 'h-10 lg:h-10'
            }`}
          />

          {/* Messages - Show only when messages exist */}
          <div
            className={`flex flex-col transition-all duration-700 ease-in-out ${
              hasMessages
                ? 'flex-1 overflow-hidden pt-14 opacity-100'
                : 'h-0 overflow-hidden opacity-0'
            }`}
          >
            <AgentChatMessages
              messages={messages}
              isWorking={isWorking}
              isLoading={isLoading}
              lastUpdated={lastUpdated}
              className="sm:px-0"
            />
          </div>

          {/* Input - Always visible */}
          <div
            className={`w-full transition-all duration-700 ${
              hasMessages ? 'pb-4' : 'pb-0 lg:pb-8'
            }`}
          >
            {structureData &&
              (!!structureData.variants.length ||
                !!Object.keys(structureData.dynamicPage).length) && (
                <DynamicPageDraftCard
                  structureData={structureData}
                  onGenerate={openVariantOriginModal}
                  onEdit={handleEditVariant}
                  onEditConstraints={handleEditConstraints}
                  onDelete={handleDeleteVariant}
                  onSettings={handleSettings}
                  onGoToPage={handleGoToPage}
                  generatingVariantId={generatingVariantId ?? undefined}
                  disabled={isDraftCardDisabled}
                />
              )}
            <StillAnalyzingAgentChatInputWrapper>
              <AgentChatInput
                collapsed={hasMessages}
                onSendMessage={sendMessage}
                onStopMessage={stopStreaming}
                className={'w-full bg-transparent'}
                size={hasMessages ? 'sm' : 'lg'}
                placeholder="Describe the funnel you would like me to create"
                disabled={isLoading}
                isStreaming={isStreaming}
                contextTypes={[
                  { type: 'file', location: 'dropdown' },
                  { type: 'asset', location: 'dropdown' },
                  {
                    type: 'campaign',
                    location: hasMessages ? 'dropdown' : 'inline',
                  },
                ]}
                promptContexts={promptContexts}
                setPromptContexts={setPromptContexts}
                updatePromptContext={updatePromptContext}
                addPromptContext={addPromptContext}
                userMessageHistory={userMessageHistory}
              />
            </StillAnalyzingAgentChatInputWrapper>
          </div>

          {/* Bottom spacer - grows when no messages to center content */}
          <div
            className={`transition-all duration-700 ease-in-out ${
              hasMessages ? 'flex-none' : 'flex-1'
            }`}
          />
        </div>
      </div>
    </>
  );
}

export const AiAssistant = () => {
  return (
    <AgentChatProvider agentName="assistantAgent">
      <AiAssistantContent />
    </AgentChatProvider>
  );
};
